import { Box, Button, DialogActions, Modal, Stack, TextField, Typography } from '@mui/material'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { inputSchema } from '../schema/inputSchema'
import { LanguageContext } from '../util/LanguageContext'

type Props = {
  open: boolean
  title: string
  preText: string
  postText: string
  onClose: () => void
  onSave: (newPreText: string, newPostText: string) => void
}

export const ConversionModalEditDialog: React.FC<Props> = ({
  open,
  title,
  preText,
  postText,
  onClose,
  onSave,
}) => {
  const translation = useContext(LanguageContext)

  const [isOpen, setIsOpen] = useState(open)
  const [inputPreText, setInputPreText] = useState(preText)
  const [inputPostText, setInputPostText] = useState(postText)
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)

  const inputPreRef = useRef<HTMLInputElement | null>(null)
  const inputPostRef = useRef<HTMLInputElement | null>(null)

  const [errorPreMessage, setErrorPreMessage] = useState('')
  const [errorPostMessage, setErrorPostMessage] = useState('')
  const [preFlag, setPreFlag] = useState(!!preText)
  const [postFlag, setPostFlag] = useState(!!postText)

  useEffect(() => {
    setIsOpen(open)
  }, [open])

  useEffect(() => {
    setInputPreText(preText)
    setInputPostText(postText)
    setPreFlag(!!preText)
    setPostFlag(!!postText)
  }, [preText, postText])

  const preValidate = async (value: string) => {
    try {
      await inputSchema.validate(value)
      setErrorPreMessage('')
      if (postFlag) setIsButtonDisabled(false)
    } catch (err: any) {
      setErrorPreMessage(err.message)
      setIsButtonDisabled(true)
    }
  }

  const postValidate = async (value: string) => {
    try {
      await inputSchema.validate(value)
      setErrorPostMessage('')
      if (preFlag) setIsButtonDisabled(false)
    } catch (err: any) {
      setErrorPostMessage(err.message)
      setIsButtonDisabled(true)
    }
  }

  const handlePreTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const v = event.target.value
    setIsButtonDisabled(true)
    setPreFlag(v !== '')
    setInputPreText(v)
    preValidate(v)
  }

  const handlePostTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const v = event.target.value
    setIsButtonDisabled(true)
    setPostFlag(v !== '')
    setInputPostText(v)
    postValidate(v)
  }

  const handleClose = (_event: any, reason?: string) => {
    if (reason === 'backdropClick') return
    onClose()
    setIsOpen(false)
  }

  const handleSave = () => {
    if (!inputPreText || !inputPostText) return
    onSave(inputPreText, inputPostText)
    onClose()
    setIsOpen(false)
  }

  useEffect(() => {
    if (inputPreText && inputPostText && !errorPreMessage && !errorPostMessage) {
      setIsButtonDisabled(false)
    } else {
      setIsButtonDisabled(true)
    }
  }, [inputPreText, inputPostText, errorPreMessage, errorPostMessage])

  useEffect(() => {
    if (isOpen && inputPreRef.current) {
      inputPreRef.current.focus()
    }
  }, [isOpen])

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '332px',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          height: '598px',
          width: '640px',
          bgcolor: 'background.paper',
          borderRadius: '10px',
          boxShadow: '0px 2px 2px #00000014',
        }}
      >
        <Stack justifyContent="center" alignItems="center">
          <Typography
            sx={{ mt: '48px', font: 'normal normal bold 32px/48px BIZ UDGothic', textAlign: 'left' }}
          >
            {translation['button_edit']}
          </Typography>
          <Typography
            sx={{
              mt: '18px',
              mb: '18px',
              mr: '50px',
              ml: '50px',
              color: '#282828',
              font: 'normal normal normal 21px/32px Noto Sans JP',
              textAlign: 'center',
            }}
          >
            {`${translation['label_edit_name']}`.replace('XX', title || '')}
          </Typography>

          <p style={{ top: '10px', width: '480px' }}>{translation['label_force_word_before']}</p>
          <TextField
            label=""
            value={inputPreText}
            error={!!errorPreMessage}
            helperText={errorPreMessage ? translation[errorPreMessage] : ' '}
            inputRef={inputPreRef}
            onChange={handlePreTextChange}
            InputProps={{
              sx: {
                font: 'normal normal normal 21px/34px BIZ UDPGothic',
                borderRadius: '10px',
                height: '72px',
                width: '480px',
                pl: '8px',
                background: '#F4F4F4',
              },
            }}
            sx={{
              mt: '5px',
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': { borderColor: errorPreMessage ? 'red' : '#282828' },
              },
            }}
          />

          <p style={{ width: '480px' }}>{translation['label_force_word_after']}</p>
          <TextField
            label=""
            value={inputPostText}
            error={!!errorPostMessage}
            helperText={errorPostMessage ? translation[errorPostMessage] : ' '}
            inputRef={inputPostRef}
            onChange={handlePostTextChange}
            InputProps={{
              sx: {
                font: 'normal normal normal 21px/34px BIZ UDPGothic',
                borderRadius: '10px',
                height: '72px',
                width: '480px',
                pl: '8px',
                background: '#F4F4F4',
              },
            }}
            sx={{
              mt: '5px',
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': { borderColor: errorPostMessage ? 'red' : '#282828' },
              },
            }}
          />

          <DialogActions sx={{ mt: '30px' }}>
            <Button
              onClick={onClose}
              sx={{
                height: '49px',
                width: '181px',
                font: 'normal normal bold 21px/29px Noto Sans JP',
                color: '#282828',
                background: '#FFFFFF 0% 0% no-repeat padding-box',
                border: '2px solid #282828',
                borderRadius: '10px',
                mt: '5px',
                mr: '30px',
                '&:hover': { background: '#FFFFFF', opacity: 0.8 },
              }}
            >
              {translation['button_cancel']}
            </Button>
            <Button
              type="submit"
              disabled={isButtonDisabled}
              onClick={handleSave}
              sx={{
                height: '49px',
                width: '181px',
                font: 'normal normal bold 21px/29px Noto Sans JP',
                color: isButtonDisabled ? '#CACACA' : '#FFFFFF',
                background: isButtonDisabled ? '#F4F4F4 0% 0% no-repeat padding-box' : '#282828',
                borderRadius: '10px',
                mt: '5px',
                '&:hover': { backgroundColor: '#282828', opacity: 0.8, boxShadow: 0 },
              }}
            >
              {translation['button_save']}
            </Button>
          </DialogActions>
        </Stack>
      </Box>
    </Modal>
  )
}